/**
 * qrDownload.js — pure, framework-free helpers for exporting a rendered QR
 * <svg> as a downloadable SVG or PNG, entirely client-side (zero server round
 * trip, zero raster asset shipped). Wave 5 "the share moment".
 *
 * The SVG we export is fully self-contained — every module rect and the brand
 * mark are inline vector, with NO external <image href>/font/CSS reference — so
 * both the SVG serialization and the canvas rasterization are safe and never
 * taint the canvas (which would block toBlob()/toDataURL()).
 *
 * Split out from the React component so the string-building logic is unit
 * testable in jsdom (the canvas raster path is exercised in the Playwright pass,
 * jsdom has no real canvas).
 */

const SVG_XML_HEADER = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n';

/** Slug a wheel name/id into a safe, lowercase filename stem. */
export function qrFilenameStem(wheel) {
  const raw =
    (wheel && (wheel.wheel_name || wheel.name || wheel.id)) != null
      ? String(wheel.wheel_name || wheel.name || wheel.id)
      : "wheel";
  const slug = raw
    .toLowerCase()
    .replace(/<[^>]*>/g, "") // strip any HTML from rich-text names
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `iyuga-${slug || "wheel"}-qr`;
}

/**
 * Serialize a live <svg> DOM node to a standalone, namespaced SVG string.
 * Guarantees the xmlns attribute (required for the file to open standalone) and
 * prepends the XML prolog.
 */
export function serializeSvg(svgEl) {
  if (!svgEl) throw new Error("serializeSvg: no <svg> node");
  const clone = svgEl.cloneNode(true);
  if (!clone.getAttribute("xmlns")) {
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  }
  if (!clone.getAttribute("xmlns:xlink")) {
    clone.setAttribute("xmlns:xlink", "http://www.w3.org/1999/xlink");
  }
  const body = new XMLSerializer().serializeToString(clone);
  return SVG_XML_HEADER + body;
}

/** Wrap an SVG string as a UTF-8 data URI usable as an <img> src. */
export function svgStringToDataUri(svgString) {
  return "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
}

/**
 * Trigger a browser download for a Blob (or string) under `filename`.
 * Returns the object URL it created so callers/tests can assert + revoke it.
 */
export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return url;
}

/** Download a serialized <svg> node as an .svg file. */
export function downloadSvg(svgEl, filename) {
  const svgString = serializeSvg(svgEl);
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  return downloadBlob(blob, filename);
}

/**
 * Rasterize a serialized <svg> node to a PNG Blob at `pixelSize` (print
 * resolution) and download it. Fully client-side via an offscreen canvas.
 * Resolves once the download is triggered; rejects if the image fails to load.
 */
export function downloadPng(svgEl, filename, pixelSize = 1024) {
  const svgString = serializeSvg(svgEl);
  const dataUri = svgStringToDataUri(svgString);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = pixelSize;
        canvas.height = pixelSize;
        const ctx = canvas.getContext("2d");
        // Solid white ground so the PNG is never transparent (scanners + print).
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, pixelSize, pixelSize);
        ctx.drawImage(img, 0, 0, pixelSize, pixelSize);
        const done = (blob) => {
          if (!blob) return reject(new Error("PNG export failed"));
          downloadBlob(blob, filename);
          resolve(blob);
        };
        if (canvas.toBlob) canvas.toBlob(done, "image/png");
        else {
          // jsdom / very old browsers: derive a Blob from the data URL.
          const b64 = canvas.toDataURL("image/png").split(",")[1];
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          done(new Blob([bytes], { type: "image/png" }));
        }
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => reject(new Error("Could not load SVG for PNG export"));
    img.src = dataUri;
  });
}
